import React from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Link } from '@inertiajs/react'
import {
  Layers, BookOpen, Bookmark, FileText, Award, GraduationCap,
  HelpCircle, CheckCircle2, AlertCircle, FileCheck, Coins, Users,
  Plus, ExternalLink, ArrowRight, Activity, Clock, Target, Sparkles, ChevronRight
} from 'lucide-react'

interface RecentAttempt {
  id: number
  user_id: number
  user_name: string
  test_id: number
  test_title: string
  score: number
  max_score: number
  accuracy: number
  time_spent_seconds: number
  completed_at: string
}

interface Props {
  stats: {
    questions: number
    pending_review: number
    approved_questions?: number
    pyq_questions?: number
    exemplar_questions?: number
    tests: number
    published_tests?: number
    draft_tests?: number
    total_attempts?: number
    completed_attempts?: number
    avg_accuracy?: number
    total_time_spent_seconds?: number
    users: number
    students_count?: number
    credits_spent?: number
    standards?: number
    subjects?: number
    chapters?: number
    sections?: number
    exams?: number
    syllabi?: number
  }
  recent_attempts?: RecentAttempt[]
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0m'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60)
    const remMins = mins % 60
    return `${hrs}h ${remMins}m`
  }
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

export default function AdminDashboard({ stats, recent_attempts = [] }: Props) {
  const returnToParam = encodeURIComponent('/admin')

  const taxonomyItems = [
    { title: 'Standards', count: stats?.standards ?? 0, href: `/admin/standards?return_to=${returnToParam}`, icon: GraduationCap, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-950/40' },
    { title: 'Subjects', count: stats?.subjects ?? 0, href: `/admin/subjects?return_to=${returnToParam}`, icon: BookOpen, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
    { title: 'Chapters', count: stats?.chapters ?? 0, href: `/admin/chapters?return_to=${returnToParam}`, icon: Bookmark, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/40' },
    { title: 'Sections', count: stats?.sections ?? 0, href: `/admin/sections?return_to=${returnToParam}`, icon: FileText, color: 'text-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-950/40' },
    { title: 'Exams', count: stats?.exams ?? 0, href: `/admin/exams?return_to=${returnToParam}`, icon: Award, color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-950/40' },
    { title: 'Syllabi', count: stats?.syllabi ?? 0, href: `/admin/syllabi?return_to=${returnToParam}`, icon: Layers, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/40' },
  ]

  const mobileTaxonomyItems = [
    { title: 'Standards', count: stats?.standards ?? 0, href: `/admin/standards?return_to=${returnToParam}`, icon: GraduationCap },
    { title: 'Subjects', count: stats?.subjects ?? 0, href: `/admin/subjects?return_to=${returnToParam}`, icon: BookOpen },
    { title: 'Chapters', count: stats?.chapters ?? 0, href: `/admin/chapters?return_to=${returnToParam}`, icon: Bookmark },
    { title: 'Sections', count: stats?.sections ?? 0, href: `/admin/sections?return_to=${returnToParam}`, icon: FileText },
    { title: 'Exams', count: stats?.exams ?? 0, href: `/admin/exams?return_to=${returnToParam}`, icon: Target },
    { title: 'Syllabi', count: stats?.syllabi ?? 0, href: `/admin/syllabi?return_to=${returnToParam}`, icon: Layers },
  ]

  return (
    <AppShell>
      {/* ========================================================================= */}
      {/* MOBILE DASHBOARD LAYOUT (Screens < lg)                                    */}
      {/* Dedicated handy mobile UX with thumb-friendly controls and scannable cards*/}
      {/* ========================================================================= */}
      <div className="lg:hidden space-y-4">
        {/* Mobile Header */}
        <div className="pb-1 border-b border-border/40">
          <h1 className="text-xl font-black text-foreground tracking-tight">Admin Overview</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Platform summary & live student activity</p>
        </div>

        {/* Pending Review Urgent Banner (if any) */}
        {(stats?.pending_review ?? 0) > 0 && (
          <Link
            href={`/admin/questions?statuses[]=pending_review&return_to=${returnToParam}`}
            className="flex items-center justify-between p-2.5 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive shadow-xs transition-colors hover:bg-destructive/15"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1 rounded-md bg-destructive/15 text-destructive shrink-0">
                <AlertCircle className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold leading-tight truncate">
                  {stats.pending_review} Question{stats.pending_review > 1 ? 's' : ''} Pending Review
                </p>
              </div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-80" />
          </Link>
        )}

        {/* Unified Platform Metrics Card Divided into 2 Parts */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-extrabold text-muted-foreground uppercase tracking-widest">Platform Metrics</span>
          </div>

          <Card className="bg-card border border-border rounded-xl shadow-2xs overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-border/50">
              {/* Left Column: Questions & Attempts */}
              <div className="divide-y divide-border/30">
                {/* Questions */}
                <Link
                  href={`/admin/questions?return_to=${returnToParam}`}
                  className="p-2.5 hover:bg-muted/40 transition-colors block group"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 group-hover:text-primary transition-colors">
                      <HelpCircle className="h-3.5 w-3.5 text-primary" />
                      Questions
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                  </div>
                  <div className="text-lg font-black text-foreground">{(stats?.questions ?? 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                    <span className="text-foreground font-semibold">{stats?.approved_questions ?? 0}</span> app.
                    <span> · </span>
                    <span className="text-foreground font-semibold">{stats?.pyq_questions ?? 0}</span> PYQ
                  </div>
                </Link>

                {/* Attempts */}
                <Link
                  href={`/admin/tests?return_to=${returnToParam}`}
                  className="p-2.5 hover:bg-muted/40 transition-colors block group"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 group-hover:text-primary transition-colors">
                      <Activity className="h-3.5 w-3.5 text-primary" />
                      Attempts
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                  </div>
                  <div className="text-lg font-black text-foreground">{(stats?.total_attempts ?? 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                    <span className="text-foreground font-semibold">{stats?.completed_attempts ?? 0}</span> done
                    <span> · </span>
                    <span className="text-foreground font-semibold">{stats?.avg_accuracy ?? 0}%</span> acc.
                  </div>
                </Link>
              </div>

              {/* Right Column: Exams & Users */}
              <div className="divide-y divide-border/30">
                {/* Exams */}
                <Link
                  href={`/admin/exams?return_to=${returnToParam}`}
                  className="p-2.5 hover:bg-muted/40 transition-colors block group"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 group-hover:text-primary transition-colors">
                      <Target className="h-3.5 w-3.5 text-primary" />
                      Exams
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                  </div>
                  <div className="text-lg font-black text-foreground">{(stats?.exams ?? 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                    <span className="text-foreground font-semibold">{stats?.tests ?? 0}</span> tests
                    <span> · </span>
                    <span className="text-foreground font-semibold">{stats?.published_tests ?? 0}</span> live
                  </div>
                </Link>

                {/* Users */}
                <Link
                  href={`/admin/users?return_to=${returnToParam}`}
                  className="p-2.5 hover:bg-muted/40 transition-colors block group"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 group-hover:text-primary transition-colors">
                      <Users className="h-3.5 w-3.5 text-primary" />
                      Users
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                  </div>
                  <div className="text-lg font-black text-foreground">{(stats?.users ?? 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                    <span className="text-foreground font-semibold">{stats?.students_count ?? 0}</span> students
                    <span> · </span>
                    <span className="text-foreground font-semibold">{stats?.credits_spent ?? 0}</span> cr.
                  </div>
                </Link>
              </div>
            </div>
          </Card>
        </div>

        {/* Unified Taxonomy Card Divided into 2 Parts */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-extrabold text-muted-foreground uppercase tracking-widest">Curriculum Taxonomy</span>
          </div>

          <Card className="bg-card border border-border rounded-xl shadow-2xs overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-border/50">
              {/* Column 1: Exams, Syllabi, Standards */}
              <div className="divide-y divide-border/30">
                {[
                  { title: 'Exams', count: stats?.exams ?? 0, href: `/admin/exams?return_to=${returnToParam}`, icon: Target },
                  { title: 'Syllabi', count: stats?.syllabi ?? 0, href: `/admin/syllabi?return_to=${returnToParam}`, icon: Layers },
                  { title: 'Standards', count: stats?.standards ?? 0, href: `/admin/standards?return_to=${returnToParam}`, icon: GraduationCap },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.title}
                      href={item.href}
                      className="flex items-center justify-between px-2.5 py-2 hover:bg-muted/40 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="text-[11px] font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                          {item.title}
                        </span>
                      </div>
                      <span className="text-[11px] font-bold text-foreground shrink-0 ml-1">
                        {item.count}
                      </span>
                    </Link>
                  )
                })}
              </div>

              {/* Column 2: Subjects, Chapters, Sections */}
              <div className="divide-y divide-border/30">
                {[
                  { title: 'Subjects', count: stats?.subjects ?? 0, href: `/admin/subjects?return_to=${returnToParam}`, icon: BookOpen },
                  { title: 'Chapters', count: stats?.chapters ?? 0, href: `/admin/chapters?return_to=${returnToParam}`, icon: Bookmark },
                  { title: 'Sections', count: stats?.sections ?? 0, href: `/admin/sections?return_to=${returnToParam}`, icon: FileText },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.title}
                      href={item.href}
                      className="flex items-center justify-between px-2.5 py-2 hover:bg-muted/40 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="text-[11px] font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                          {item.title}
                        </span>
                      </div>
                      <span className="text-[11px] font-bold text-foreground shrink-0 ml-1">
                        {item.count}
                      </span>
                    </Link>
                  )
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Student Test Activity: Sleek Compact Feed */}
        {recent_attempts.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-extrabold text-muted-foreground uppercase tracking-widest">Recent Activity</span>
              <Button variant="ghost" size="sm" asChild className="h-5 px-1 text-[10px] text-muted-foreground hover:text-foreground">
                <Link href={`/admin/users?return_to=${returnToParam}`}>
                  View Users <ArrowRight className="h-3 w-3 ml-0.5" />
                </Link>
              </Button>
            </div>

            <div className="bg-card border border-border rounded-xl divide-y divide-border/40 overflow-hidden shadow-2xs">
              {recent_attempts.slice(0, 5).map((att) => (
                <Link
                  key={att.id}
                  href={`/student/attempts/${att.id}/result?return_to=${returnToParam}`}
                  className="flex items-center gap-2.5 p-2.5 hover:bg-muted/40 transition-colors group block"
                >
                  {/* Left: Accuracy Badge */}
                  <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex flex-col items-center justify-center shrink-0">
                    <span className="text-[11px] font-black leading-none">
                      {Math.round(att.accuracy ?? 0)}%
                    </span>
                    <span className="text-[7px] font-bold uppercase opacity-80 leading-none mt-0.5">
                      acc
                    </span>
                  </div>

                  {/* Center: Student Name + Test Title + Micro stats */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-xs font-bold text-foreground truncate group-hover:text-primary transition-colors">
                        {att.user_name}
                      </span>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {att.completed_at}
                      </span>
                    </div>

                    <p className="text-[11px] text-muted-foreground truncate leading-tight mt-0.5">
                      {att.test_title}
                    </p>

                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-1">
                      <span className="font-semibold text-foreground">
                        {att.score}/{att.max_score} pts
                      </span>
                      <span>·</span>
                      <span>{formatDuration(att.time_spent_seconds)}</span>
                    </div>
                  </div>

                  {/* Right: Chevron Arrow */}
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* DESKTOP DASHBOARD LAYOUT (Screens >= lg)                                  */}
      {/* ========================================================================= */}
      <div className="hidden lg:block space-y-6">
        {/* Header & Quick Action Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">Platform overview, taxonomy hierarchy, and live student analytics</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" asChild className="gap-1.5 font-semibold">
              <Link href={`/admin/questions/new?return_to=${returnToParam}`}>
                <Plus className="h-4 w-4" /> New Question
              </Link>
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-1.5 font-medium">
              <Link href={`/admin/tests?return_to=${returnToParam}`}>
                <FileCheck className="h-4 w-4" /> Create Test
              </Link>
            </Button>
            <Button size="sm" variant="outline" asChild className="gap-1.5 font-medium">
              <Link href={`/admin/users?return_to=${returnToParam}`}>
                <Users className="h-4 w-4" /> Manage Users
              </Link>
            </Button>
          </div>
        </div>

        {/* Primary Platform Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* 1. Question Bank Card */}
          <Card className="hover:border-primary/50 transition-colors shadow-2xs">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <HelpCircle className="h-4 w-4 text-primary" /> Question Bank
                </CardTitle>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" asChild>
                  <Link href={`/admin/questions?return_to=${returnToParam}`}>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-baseline justify-between">
                <Link href={`/admin/questions?return_to=${returnToParam}`} className="text-3xl font-bold hover:underline">
                  {(stats?.questions ?? 0).toLocaleString()}
                </Link>
                {(stats?.pending_review ?? 0) > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-xs gap-1 font-semibold" asChild>
                    <Link href={`/admin/questions?statuses[]=pending_review&return_to=${returnToParam}`}>
                      <AlertCircle className="h-3 w-3 text-amber-500" />
                      {stats.pending_review} Pending Review
                    </Link>
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-3 gap-1 pt-2 border-t text-[11px] text-muted-foreground text-center">
                <div className="bg-muted/40 p-1 rounded">
                  <div className="font-semibold text-foreground">{stats?.approved_questions ?? 0}</div>
                  <div>Approved</div>
                </div>
                <div className="bg-muted/40 p-1 rounded">
                  <div className="font-semibold text-foreground">{stats?.pyq_questions ?? 0}</div>
                  <div>PYQs</div>
                </div>
                <div className="bg-muted/40 p-1 rounded">
                  <div className="font-semibold text-foreground">{stats?.exemplar_questions ?? 0}</div>
                  <div>Exemplars</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2. Tests & Mocks Card */}
          <Card className="hover:border-primary/50 transition-colors shadow-2xs">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <FileCheck className="h-4 w-4 text-emerald-500" /> Tests & Mocks
                </CardTitle>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" asChild>
                  <Link href={`/admin/tests?return_to=${returnToParam}`}>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link href={`/admin/tests?return_to=${returnToParam}`} className="text-3xl font-bold hover:underline block">
                {(stats?.tests ?? 0).toLocaleString()}
              </Link>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t text-[11px] text-muted-foreground text-center">
                <div className="bg-emerald-50/50 dark:bg-emerald-950/30 p-1.5 rounded border border-emerald-100 dark:border-emerald-900">
                  <div className="font-semibold text-emerald-700 dark:text-emerald-300">{stats?.published_tests ?? 0}</div>
                  <div>Published</div>
                </div>
                <div className="bg-muted/40 p-1.5 rounded">
                  <div className="font-semibold text-foreground">{stats?.draft_tests ?? 0}</div>
                  <div>Drafts</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3. Student Attempts & Accuracy Card */}
          <Card className="hover:border-primary/50 transition-colors shadow-2xs">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Activity className="h-4 w-4 text-indigo-500" /> Student Attempts
                </CardTitle>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" asChild>
                  <Link href={`/admin/tests?return_to=${returnToParam}`}>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-3xl font-bold">{(stats?.total_attempts ?? 0).toLocaleString()}</div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t text-[11px] text-muted-foreground text-center">
                <div className="bg-indigo-50/50 dark:bg-indigo-950/30 p-1.5 rounded border border-indigo-100 dark:border-indigo-900">
                  <div className="font-semibold text-indigo-700 dark:text-indigo-300">{stats?.completed_attempts ?? 0}</div>
                  <div>Completed</div>
                </div>
                <div className="bg-muted/40 p-1.5 rounded">
                  <div className="font-semibold text-foreground">{stats?.avg_accuracy ?? 0}%</div>
                  <div>Avg Accuracy</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4. Users & Platform Revenue Card */}
          <Card className="hover:border-primary/50 transition-colors shadow-2xs">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-amber-500" /> Registered Users
                </CardTitle>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" asChild>
                  <Link href={`/admin/users?return_to=${returnToParam}`}>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link href={`/admin/users?return_to=${returnToParam}`} className="text-3xl font-bold hover:underline block">
                {(stats?.users ?? 0).toLocaleString()}
              </Link>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t text-[11px] text-muted-foreground text-center">
                <div className="bg-muted/40 p-1.5 rounded">
                  <div className="font-semibold text-foreground">{stats?.students_count ?? 0}</div>
                  <div>Students</div>
                </div>
                <div className="bg-amber-50/50 dark:bg-amber-950/30 p-1.5 rounded border border-amber-100 dark:border-amber-900">
                  <div className="font-semibold text-amber-800 dark:text-amber-300">{stats?.credits_spent ?? 0}</div>
                  <div>Credits Spent</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Taxonomy Quick Access Section */}
        <div>
          <h2 className="text-lg font-semibold mb-3">Taxonomy Structure Quick Access</h2>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {taxonomyItems.map((item) => {
              const Icon = item.icon
              return (
                <Link key={item.title} href={item.href}>
                  <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full shadow-2xs">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex items-center justify-between">
                        <div className={`p-2 rounded-lg ${item.bg}`}>
                          <Icon className={`h-5 w-5 ${item.color}`} />
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-accent text-foreground">
                          {item.count}
                        </span>
                      </div>
                      <CardTitle className="text-sm font-medium mt-3">{item.title}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              )
            })}
          </div>
        </div>

        {/* Recent Student Test Activity Feed */}
        {recent_attempts.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>Recent Student Test Activity</span>
                </CardTitle>
                <Button variant="ghost" size="sm" asChild className="text-xs gap-1 text-muted-foreground">
                  <Link href={`/admin/users?return_to=${returnToParam}`}>
                    View All Users <ArrowRight className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Test Title</TableHead>
                    <TableHead className="w-28">Score</TableHead>
                    <TableHead className="w-24">Accuracy</TableHead>
                    <TableHead className="w-24">Time Taken</TableHead>
                    <TableHead className="w-36">Completed At</TableHead>
                    <TableHead className="w-12 text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent_attempts.map((att) => (
                    <TableRow key={att.id}>
                      <TableCell className="font-medium">
                        <Link href={`/admin/users/${att.user_id}?return_to=${returnToParam}`} className="hover:underline font-semibold">
                          {att.user_name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link href={`/admin/tests/${att.test_id}?return_to=${returnToParam}`} className="hover:underline line-clamp-1 max-w-xs text-xs">
                          {att.test_title}
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs font-semibold text-foreground">
                        {att.score} / {att.max_score} pts
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs font-semibold">
                          {att.accuracy ?? 0}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDuration(att.time_spent_seconds)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{att.completed_at}</TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" asChild title="View Result">
                          <Link href={`/student/attempts/${att.id}/result?return_to=${returnToParam}`}>
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  )
}
